import React, { useState, useEffect, useMemo } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import MenuIcon from "@mui/icons-material/Menu";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { useSession } from "../modules/About/Session Management/index.js";
import mapsJson from "../maps.json";

const drawerWidth = 350;

export default function Header(props) {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  let title = process.env.REACT_APP_TITLE;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  let url = process.env.PUBLIC_URL + "/TravelMidwest_Logo_White_Word.png";
  let bgcolor = "#031E2F";
  const navigate = useNavigate();

  const { handleLogout: sessionLogout } = useSession();

  useEffect(() => {
    const checkAdminStatus = () => {
      const adminStatus = sessionStorage.getItem("isAdmin") === "true";
      setIsAdminLoggedIn(adminStatus);
      const userStatus = sessionStorage.getItem("isUser") === "true";
      setIsUserLoggedIn(userStatus);
    };

    checkAdminStatus();

    const handleStorageChange = () => {
      checkAdminStatus();
    };

    const handleLoginEvent = () => {
      checkAdminStatus();
    };

    const handleUserLoginEvent = () => {
      checkAdminStatus();
    };

    const handleSessionExpired = () => {
      setIsAdminLoggedIn(false);
      setIsUserLoggedIn(false);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("adminLoginChange", handleLoginEvent);
    window.addEventListener("userLoginChange", handleUserLoginEvent);
    window.addEventListener("sessionExpired", handleSessionExpired);

    const interval = setInterval(checkAdminStatus, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adminLoginChange", handleLoginEvent);
      window.removeEventListener("userLoginChange", handleUserLoginEvent);
      window.removeEventListener("sessionExpired", handleSessionExpired);
      clearInterval(interval);
    };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAdminLogout = async () => {
    try {
      const endpoint = "/user/logout.json";
      await fetch(process.env.REACT_APP_API_HOST + endpoint, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {}

    sessionLogout();

    navigate("/");
  };

  const handleUserLogout = async () => {
    try {
      // Trip-user logout clears only the alertsLoggedIn cookie, leaving any admin session intact.
      // That is what lets an admin who used "become user" drop the impersonation and stay signed in
      // as an admin. handleAdminLogout above posts to /user/logout.json, which clears both cookies.
      const endpoint = "/user/trip/logout.json";
      await fetch(process.env.REACT_APP_API_HOST + endpoint, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {}

    sessionStorage.removeItem("isUser");
    sessionStorage.removeItem("user");
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    setIsUserLoggedIn(false);

    navigate("/");
  };

  // Static, not MUI's default fixed: a fixed bar sits at the top of the viewport and
  // covers the simulation-mode banner that Layout renders above it.  And a div, not
  // AppBar's default <header>, because Layout already wraps this in the <header>.
  if (matches) {
    return (
      <AppBar
        component="div"
        position="static"
        sx={{ height: 80, backgroundColor: bgcolor }}
      >
        <Box sx={{ display: "flex", p: 1, height: "100%" }}>
          <Box
            component={Link}
            to={"/"}
            sx={{
              flexGrow: 3,
              backgroundImage: "url(" + url + ")",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
              textAlign: "left",
              padding: 1,
              maxWidth: 300,
            }}
            title={title}
          ></Box>
          <Box sx={{ flexGrow: 1, flexShrink: 1, pt: 1.5 }}>
            <SelectionMenu
              {...props}
              setMobileOpen={setMobileOpen}
              handleDrawerToggle={handleDrawerToggle}
              mobileOpen={mobileOpen}
            ></SelectionMenu>
          </Box>
          {isAdminLoggedIn && (
            <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
              <AdminProfile handleLogout={handleAdminLogout} />
            </Box>
          )}
          {!isAdminLoggedIn && isUserLoggedIn && (
            <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
              <UserProfile handleLogout={handleUserLogout} />
            </Box>
          )}
        </Box>
      </AppBar>
    );
  } else {
    return (
      <AppBar
        component="div"
        position="static"
        sx={{ height: 60, minHeight: 60, backgroundColor: bgcolor }}
      >
        <Box sx={{ display: "flex", p: 1, height: "100%" }}>
          <Box
            component={Link}
            to={"/"}
            sx={{
              flexGrow: 1,
              backgroundImage: "url(" + url + ")",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "left center",
              textAlign: "left",
            }}
            title={title}
          ></Box>
          {isAdminLoggedIn && (
            <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
              <AdminProfile handleLogout={handleAdminLogout} isMobile={true} />
            </Box>
          )}
          {!isAdminLoggedIn && isUserLoggedIn && (
            <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
              <UserProfile handleLogout={handleUserLogout} isMobile={true} />
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              marginRight: 1,
              textAlign: "right",
              maxWidth: 20,
              height: "100%",
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open or close sidebar"
              edge="start"
              onClick={handleDrawerToggle}
              title="Open or Close sidebar"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>
        <SelectionMenu
          {...props}
          setMobileOpen={setMobileOpen}
          handleDrawerToggle={handleDrawerToggle}
          mobileOpen={mobileOpen}
        ></SelectionMenu>
      </AppBar>
    );
  }
}

const SelectionMenu = (props) => {
  const { window } = props;
  const handleDrawerToggle = props.handleDrawerToggle;
  const mobileOpen = props.mobileOpen;
  const modules = props.modules;
  const container =
    window !== undefined ? () => window().document.body : undefined;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const menus = modules.filter((module) => module.showOnHeader);

  if (!matches) {
    return (
      <>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          anchor="right"
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: "85%",
              maxWidth: drawerWidth,
            },
          }}
        >
          <Box sx={{ overflow: "auto" }}>
            <List>
              {menus.map((menu) => {
                const isAdmin = sessionStorage.getItem("isAdmin");
                const isUser = sessionStorage.getItem("isUser");
                const shouldShowMenu =
                  isAdmin ||
                  (isUser && menu.name !== "Admin") ||
                  (!isUser && menu.name !== "Admin" && menu.name !== "User");

                if (!shouldShowMenu) return null;

                return (
                  <DropdownSubMenu
                    key={menu.name}
                    title={menu.label}
                    menuitems={menu.submodules}
                    menupath={menu.routeProps.path}
                    defaultOpen={menu.defaultOpen}
                    handleDrawerToggle={handleDrawerToggle}
                  />
                );
              })}
            </List>
          </Box>
        </Drawer>
        {/* <showMaps/> */}
      </>
    );
  } else {
    return (
      <Box sx={{ display: "flex", ml: 2, pt: 1, textAlign: "left" }}>
        {menus.map((menu) => {
          const isAdmin = sessionStorage.getItem("isAdmin") === "true";
          const isUser = sessionStorage.getItem("isUser") === "true";
          const canViewMenu =
            isAdmin ||
            (isUser && menu.name !== "Admin") ||
            (!isUser && menu.name !== "Admin" && menu.name !== "User");

          if (canViewMenu) {
            return (
              <DropdownButton
                title={menu.label}
                key={menu.name}
                menuitems={menu.submodules}
                menupath={menu.routeProps.path}
              />
            );
          }

          return null;
        })}
        {/* <showMaps/> */}
      </Box>
    );
  }
};

const SingleLink = (props) => {
  const title = props.title;
  const menupath = props.menupath;
  const location = useLocation();
  const handleDrawerToggle = props.handleDrawerToggle;

  if (location.pathname !== menupath) {
    return (
      <ListItemButton
        component={Link}
        to={menupath}
        onClick={handleDrawerToggle}
      >
        <ListItemText primary={title} />
      </ListItemButton>
    );
  }

  return null;
};

const ShowMapsLink = () => {
  let isDark = useMediaQuery("(prefers-color-scheme: dark)");

  return (
    <div>
      {mapsJson.map((link) => {
        if (link["state"] === "Illinois")
          return (
            <Box key={link.id}>
              <MenuItem sx={{ fontSize: 14, blockSize: 25 }}>
                <NavLink
                  style={{
                    marginLeft: 5,
                    color: isDark ? "white" : "black",
                    textDecoration: "none",
                  }}
                  to={`/map/${link["id"]}`}
                >
                  {" "}
                  {link["title"]}{" "}
                </NavLink>
              </MenuItem>
            </Box>
          );
        else return null;
      })}
      {mapsJson.map((link) => {
        if (link["state"] === "Indiana")
          return (
            <Box key={link.id}>
              <MenuItem sx={{ fontSize: 14, blockSize: 25 }}>
                <NavLink
                  style={{
                    marginLeft: 5,
                    color: isDark ? "white" : "black",
                    textDecoration: "none",
                  }}
                  to={`/map/${link["id"]}`}
                >
                  {" "}
                  {link["title"]}{" "}
                </NavLink>
              </MenuItem>
            </Box>
          );
        else return null;
      })}
      {mapsJson.map((link) => {
        if (link["state"] === "Wisconsin")
          return (
            <Box key={link.id}>
              <MenuItem sx={{ fontSize: 14, blockSize: 25 }}>
                <NavLink
                  style={{
                    marginLeft: 5,
                    textDecoration: "none",
                    color: isDark ? "white" : "black",
                  }}
                  to={`/map/${link["id"]}`}
                >
                  {" "}
                  {link["title"]}{" "}
                </NavLink>
              </MenuItem>
            </Box>
          );
        else return null;
      })}
    </div>
  );
};

const DropdownSubMenu = (props) => {
  const [open, setOpen] = React.useState(props.defaultOpen);
  const handleDrawerToggle = props.handleDrawerToggle;
  const title = props.title;
  const menuitems = props.menuitems;
  const items = menuitems
    ? menuitems.filter((mitem) => mitem.showOnMenu)
    : null;
  const menupath = props.menupath;
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleTTStats = useMemo(
    () =>
      debounce(() => {
        window.open(
          "https://www.travelmidweststats.com",
          "_blank",
          "noopener,noreferrer",
        );
        setAnchorEl(null);
      }, 1000),
    [],
  );

  const handleClick = () => {
    setOpen(!open);
  };

  if (title === "Maps") {
    return (
      <>
        <ListItemButton onClick={handleClick}>
          <ListItemText primary={title} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <Box onClick={handleDrawerToggle}>
              <ShowMapsLink />
            </Box>
          </List>
        </Collapse>
      </>
    );
  }

  if (null === items) {
    return <SingleLink {...props} />;
  }

  return (
    <>
      <ListItemButton onClick={handleClick}>
        <ListItemText primary={title} />
        {open ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {items.map((item) => {
            if (item.name === "Travel Time Statistics")
              return (
                <MenuItem
                  sx={{ fontSize: 14, blockSize: 25 }}
                  onClick={handleTTStats}
                  key={item.name}
                >
                  {item.label}
                </MenuItem>
              );
            else if (
              item.name !== "InfoReusePolicy" &&
              item.name !== "Registration Form"
            ) {
              return (
                <ListItemButton
                  sx={{ pl: 4 }}
                  key={item.name}
                  component={Link}
                  to={menupath + "/" + item.routeProps.path}
                  onClick={handleDrawerToggle}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            } else {
              return null;
            }
          })}
        </List>
      </Collapse>
    </>
  );
};

const DropdownButton = (props) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const title = props.title;
  const menuitems = props.menuitems;
  const items = menuitems
    ? menuitems.filter((mitem) => mitem.showOnMenu)
    : null;
  const menupath = props.menupath;
  let isDark = useMediaQuery("(prefers-color-scheme: dark)");
  const location = useLocation();

  const handleTTStats = useMemo(
    () =>
      debounce(() => {
        window.open(
          "https://www.travelmidweststats.com",
          "_blank",
          "noopener,noreferrer",
        );
        setAnchorEl(null);
      }, 1000),
    [],
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (title === "Maps") {
    return (
      <div>
        <Button
          id={"drop-button"}
          aria-controls={open ? "menu" + title : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          sx={{
            m1: 1,
            mr: 1,
            color: isDark ? "white !important" : "white !important",
          }}
        >
          {title}
        </Button>
        <Menu
          id={"menu" + title}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "drop-button",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box onClick={handleClose}>
            <ShowMapsLink />
          </Box>
        </Menu>
      </div>
    );
  }

  if (null === items && location.pathname !== menupath) {
    return (
      <Button
        id={"drop-button"}
        sx={{
          m1: 1,
          mr: 1,
          color: isDark ? "white !important" : "white !important",
        }}
        component={Link}
        to={menupath}
      >
        {title}
      </Button>
    );
  }

  if (null === items) return null;

  return (
    <div>
      <Button
        id={"drop-button"}
        aria-controls={open ? "menu" + title : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          m1: 1,
          mr: 1,
          color: isDark ? "white !important" : "white !important",
        }}
      >
        {title}
      </Button>
      <Menu
        id={"menu" + title}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "drop-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {items.map((item) => {
          if (item.name === "Travel Time Statistics")
            return (
              <MenuItem
                sx={{ fontSize: 14, blockSize: 25 }}
                onClick={handleTTStats}
                key={item.name}
              >
                {item.label}
              </MenuItem>
            );
          else if (
            item.name !== "InfoReusePolicy" &&
            item.name !== "Registration Form"
          )
            return (
              <MenuItem
                sx={{ fontSize: 14, blockSize: 25 }}
                onClick={handleClose}
                key={item.name}
                component={Link}
                to={menupath + "/" + item.routeProps.path}
              >
                {item.label}
              </MenuItem>
            );
          else return null;
        })}
      </Menu>
    </div>
  );
};

const AdminProfile = ({ handleLogout, isMobile = false }) => {
  const adminUser = React.useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("adminUser") || "{}");
    } catch {
      return {};
    }
  }, []);

  const userName = adminUser.name;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0.5,
          borderRadius: 1,
        }}
      >
        <AccountCircleIcon
          sx={{ color: "white", fontSize: isMobile ? 28 : 32 }}
        />
        {!isMobile && (
          <Typography
            variant="caption"
            sx={{
              color: "white",
              fontWeight: 500,
              fontSize: "0.7rem",
              lineHeight: 1,
              mt: 0.5,
              textAlign: "center",
            }}
          >
            {userName}
          </Typography>
        )}
      </Box>

      <Tooltip title="Logout">
        <IconButton
          color="inherit"
          onClick={() => handleLogout()}
          sx={{
            color: "white",
            padding: 0.5,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <ExitToAppIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const UserProfile = ({ handleLogout, isMobile = false }) => {
  const user = React.useMemo(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem("user") || localStorage.getItem("user") || "{}",
      );
    } catch {
      return {};
    }
  }, []);

  const userName = user.name || user.login;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0.5,
          borderRadius: 1,
        }}
      >
        <AccountCircleIcon
          sx={{ color: "white", fontSize: isMobile ? 28 : 32 }}
        />
        {!isMobile && userName && (
          <Typography
            variant="caption"
            sx={{
              color: "white",
              fontWeight: 500,
              fontSize: "0.7rem",
              lineHeight: 1,
              mt: 0.5,
              textAlign: "center",
            }}
          >
            {userName}
          </Typography>
        )}
      </Box>

      <Tooltip title="Logout">
        <IconButton
          color="inherit"
          onClick={() => handleLogout()}
          sx={{
            color: "white",
            padding: 0.5,
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <ExitToAppIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
